<template>
  <div class="content-wrapper report">
    <nav class="navbar sticky-top row">
      <div class="container">
        <insightReportComparisonWidget
          :reset-secondary-sample="resetSecondarySample"
          :link-attached-to-secondary="true"
          :samples="patientSamples"
          class="mt-3 col-12"
          :selected-option-truncate-limit="selectedOptionTruncateLimit"
          :dropdown-option-truncate-limit="dropdownOptionTruncateLimit"
          :pre-selection-samples="preSelectionSamples"
          :primary-sample-instruction-enabled="true"
          :secondary-sample-instruction-enabled="true"
          :primary-sample-instruction="primarySampleInstructions"
          :secondary-sample-instruction="secondarySampleInstructions"
          @firstSelectedSampleChanged="updatePrimarySample"
          @secondSelectedSampleChanged="updateSecondarySample"
          @compareReports="compareReports"
          @resetSecondarySampleStatus="resetSecondarySampleStatus"
        />
      </div>
    </nav>
    <div
      v-if="displayReport"
      class="mb-3 container"
    >
      {{ patientDetails.firstName }} {{ patientDetails.lastName }}
    </div>
    <microba-results
      v-if="displayReport"
      name="microbaResult"
      :primary-sample="primarySample"
      :secondary-sample="secondarySample"
      :sample-identifier="primarySample.identifier"
      :hide-header="true"
      :hide-name="false"
      :export-u-r-l="exportURL"
      @mgdbVersionsIncompatible="mgdbVersionsIncompatible"
      @serviceUnavailable="serviceUnavailable"
    />
  </div>
</template>

<script>
import 'bootstrap-vue/dist/bootstrap-vue.css'
import moment from 'moment'
import { Info } from '@microbadevs/library'
import insightReportComparisonWidget from '@microbadevs/insight-report-comparison-widget'
const MobileDetect = require('mobile-detect')

export default {
  name: 'Results',
  components: { insightReportComparisonWidget },
  data () {
    return {
      resetSecondarySample: false,
      selectedPrimarySample: null,
      selectedSecondarySample: null,
      primarySampleInstructions: {
        label: 'To compare, select an earlier report here...',
        type: 'instruction'
      },
      secondarySampleInstructions: {
        label: 'Select a later report here...',
        type: 'instruction'
      }
    }
  },
  computed: {
    primarySample () {
      return this.selectedPrimarySample || null
    },
    secondarySample () {
      return this.selectedSecondarySample || null
    },
    user () {
      return this.$store.state.GET_USER_RESPONSE
    },
    preSelectionSamples () {
      const samples = this.$route?.query?.samples || []
      const preSelectionSamples = Array.isArray(samples)
        ? samples?.map((sample) => sample.toUpperCase())
        : [samples.toUpperCase()]
      return preSelectionSamples
    },
    displayReport () {
      return Boolean(this.primarySample?.identifier)
    },
    exportURL () {
      return process.env.VUE_APP_REPORT_DOWNLOAD_ROOT
    },
    allKits () {
      return this.$store.state.GET_KITS_RESPONSE
    },
    patientDetails () {
      if (!this.preSelectionSamples?.length || !this.allKits) {
        return null
      }
      const currentSample = this.allKits.find((kit) => {
        const thisKitSampleCode =
          kit.sampleIdentifier && kit.sampleIdentifier.toUpperCase()
        return thisKitSampleCode === this.preSelectionSamples[0]
      })
      return currentSample
        ? {
          patientId: currentSample?.patientId,
          name: currentSample?.patientName,
          firstName: currentSample?.patientFirstName,
          lastName: currentSample?.patientLastName,
          dateOfBirth: currentSample?.dateOfBirth,
          parentConsented: currentSample?.parentConsented,
          patientUnderAge: currentSample?.patientUnderAge
        }
        : null
    },
    patientSamples () {
      if (!this.patientDetails) {
        return []
      }

      return this.allKits
        .filter((kit) => kit.patientId === this.patientDetails?.patientId)
        .map((kit) => {
          return {
            label: kit.sampleIdentifier,
            displayName: this.getKitName(kit),
            displayDate: kit.displayDate
              ? moment(kit.displayDate).format('YYYY-MM-DD')
              : null,
            id: kit.id,
            identifier: kit.sampleIdentifier
          }
        })
    },
    isMobile () {
      const mobileDetect = new MobileDetect(window.navigator.userAgent)
      return mobileDetect.mobile()
    },
    selectedOptionTruncateLimit () {
      return this.isMobile ? 8 : 20
    },
    dropdownOptionTruncateLimit () {
      return this.isMobile ? 16 : 16
    }
  },
  mounted () {
    this.$store.dispatch('getKits')
    window.scrollTo(0, 0)
    if (this.preSelectionSamples.length > 1) {
      setTimeout(() => {
        this.compareReports()
      }, 100)
    }
  },
  methods: {
    getKitName (kit) {
      if (kit?.name) {
        return kit.name
      }
      return kit.displayDate
        ? moment(kit.displayDate).format('MMMM Do')
        : kit.sampleIdentifier
    },
    updatePrimarySample (payload) {
      this.updateSample('primarySample', payload)
    },
    updateSecondarySample (payload) {
      if (!payload) {
        this.updateSample('secondarySample', payload)
      } else {
        this.temporarySecondarySample = payload
      }
    },
    updateSample (sample, payload) {
      switch (sample) {
      case 'primarySample':
        this.selectedPrimarySample = payload
        break
      case 'secondarySample':
        this.selectedSecondarySample = payload
        break
      }
    },
    compareReports () {
      this.selectedSecondarySample = this.temporarySecondarySample
    },
    mgdbVersionsIncompatible () {
      Info({
        text:
          'Significant improvements have been done to our system since your last report. Due to this, your old report and new report cannot be compared using this online feature. <br><br> Should you require further assistance, please contact info@microba.com (code V12V2).'
      }).then(() => {
        this.selectedSecondarySample = null
        this.resetSecondarySample = true
      })
    },
    resetSecondarySampleStatus () {
      this.resetSecondarySample = false
    },
    serviceUnavailable (...args) {
      this.$store.dispatch('maintenance/show', ...args)
    }
  }
}
</script>

<style lang="scss">
  .tab-overview {
    margin-right: 6px;
  }

  .insight-report-comparison-widget {
    .instructionItem {
      padding-top: 5px;
      padding-left: 30px;
      margin-bottom: 1.5em;
      font-style: italic;
      color: #666;
      cursor: default;
    }

    .badge-primary {
      background-color: #5d4164 !important;
      color: white;
    }

    .badge-secondary {
      background-color: #ff8708 !important;
      color: white;
    }

    .btn-disabled {
      border-radius: 5px;
    }

    .form-group-select {
      margin: 0;

      .v-select {
        .dropdown-toggle {
          .clear {
            display: initial;

            span {
              font-size: 40px;
              margin-right: 20px;
            }
          }

          .open-indicator {
            margin-right: 20px;

            &::before {
              border-color: #5e3f63;
            }
          }

          border: 1px solid #e2e6e8;
        }

        .dropdown-menu {
          border: 1px solid #e2e6e8;

          .sampleItem {
            .displayDate {
              color: #bababa;
            }
          }

          .no-options {
            text-align: left;

            hr {
              margin: 0;
            }

            span {
              div {
                font-size: 1.2em;
                padding: 10px 10px 10px 30px;
                cursor: default;

                &.block {
                  cursor: pointer;
                  color: #9fa5b1;

                  &:hover {
                    background-color: #e8eced;
                    color: #38455d;
                    font-weight: 400;
                  }
                }
              }
            }
          }

          a {
            color: black;
            font-size: 1.2em;
            font-weight: lighter;
            text-transform: none;
            padding: 0;

            .sampleItem {
              padding: 10px 10px 10px 30px;

              &:hover {
                background: #5897fb;
                color: white;

                .displayDate {
                  color: white;
                }
              }
            }

            .instructionItem {
              &:hover {
                background: none;
              }
            }
          }

          .highlight {
            a {
              background: none;
            }
          }
        }
      }
    }
  }

  .report {
    .sample-badge {
      margin: auto 50px auto 20px;
      padding: 0.25rem 1.5rem;

      &__primary {
        background-color: #5e3f63;
      }

      &__secondary {
        background-color: #ff8708;
      }

      &-label {
        &__primary {
          font-size: 16px;
          color: white;
        }

        &__secondary {
          font-size: 16px;
          color: white;
        }
      }
    }
  }
</style>
<style scoped lang="scss">
  .content-wrapper {
    margin-top: 20px !important;
    background-color: #fff !important;

    .navbar {
      background-color: white;
      z-index: 1;
    }
  }
</style>
